// src/redux/slices/servicesAdminSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { servicesService } from '../../services/servicesService.js'
import { handleApiError } from '../../utils/handleApiError.js'

export const fetchServicesAdmin = createAsyncThunk(
    'servicesAdmin/fetchServicesAdmin',
    async (params = {}, { rejectWithValue }) => {
        try {
            const response = await servicesService.getServicesAdmin(params)
            return {
                services: response.data.services,
                pagination: response.data.pagination
            }
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const createService = createAsyncThunk(
    'servicesAdmin/createService',
    async (serviceData, { rejectWithValue }) => {
        try {
            const response = await servicesService.createService(serviceData)
            return response.data.service
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const updateService = createAsyncThunk(
    'servicesAdmin/updateService',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const response = await servicesService.updateService(id, data)
            return response.data.service
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const deleteService = createAsyncThunk(
    'servicesAdmin/deleteService',
    async (id, { rejectWithValue }) => {
        try {
            await servicesService.deleteService(id)
            return id
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

export const toggleServiceStatus = createAsyncThunk(
    'servicesAdmin/toggleServiceStatus',
    async (id, { rejectWithValue }) => {
        try {
            const response = await servicesService.toggleServiceStatus(id)
            return response.data.service
        } catch (error) {
            return rejectWithValue(handleApiError(error))
        }
    }
)

const servicesAdminSlice = createSlice({
    name: 'servicesAdmin',
    initialState: {
        services: [],
        currentService: null,
        loading: false,
        pagination: {
            page: 1,
            limit: 10,
            total: 0,
            totalPages: 0,
            hasNext: false,
            hasPrev: false
        },
        error: null
    },
    reducers: {
        clearCurrentService: (state) => {
            state.currentService = null
        },
        clearError: (state) => {
            state.error = null
        },
        setPagination: (state, action) => {
            state.pagination = { ...state.pagination, ...action.payload }
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch Services Admin
            .addCase(fetchServicesAdmin.pending, (state) => {
                state.loading = true
                state.error = null
            })
            .addCase(fetchServicesAdmin.fulfilled, (state, action) => {
                state.loading = false
                state.services = action.payload.services
                state.pagination = action.payload.pagination
                state.error = null
            })
            .addCase(fetchServicesAdmin.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })

            // Create Service
            .addCase(createService.pending, (state) => {
                state.loading = true
            })
            .addCase(createService.fulfilled, (state, action) => {
                state.loading = false
                state.services.unshift(action.payload)
                state.error = null
            })
            .addCase(createService.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })

            // Update Service
            .addCase(updateService.pending, (state) => {
                state.loading = true
            })
            .addCase(updateService.fulfilled, (state, action) => {
                state.loading = false
                const index = state.services.findIndex(service => service._id === action.payload._id)
                if (index !== -1) {
                    state.services[index] = action.payload
                }
                state.error = null
            })
            .addCase(updateService.rejected, (state, action) => {
                state.loading = false
                state.error = action.payload
            })

            // Delete Service
            .addCase(deleteService.fulfilled, (state, action) => {
                state.services = state.services.filter(service => service._id !== action.payload)
                state.error = null
            })

            // Toggle Service Status
            .addCase(toggleServiceStatus.fulfilled, (state, action) => {
                const index = state.services.findIndex(service => service._id === action.payload._id)
                if (index !== -1) {
                    state.services[index] = action.payload
                }
                state.error = null
            })
    }
})

export const { clearCurrentService, clearError, setPagination } = servicesAdminSlice.actions
export default servicesAdminSlice.reducer